import { ApolloClient, gql } from '@apollo/client'
import {
  GetParentalControlsLanguagesForContentQuery,
  GetParentalControlsLanguagesForContentQueryVariables,
  GetParentalControlsLanguagesForProjectQuery,
  GetParentalControlsLanguagesForProjectQueryVariables,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { Maybe } from '@/utils/types'

const GET_PARENTAL_CONTROLS_LANGUAGES_FOR_CONTENT = gql`
  query GetParentalControlsLanguagesForContent($id: ID!) {
    getContent(id: $id) {
      ... on Node {
        id
        __typename
      }
      ... on ContentWatchable {
        muteAllSwearsAvailability
      }
    }
    user {
      __typename
      id
      uuid
      muteAllSwearingPreferenceEnabled
    }
  }
`

const GET_PARENTAL_CONTROLS_LANGUAGES_FOR_PROJECT = gql`
  query GetParentalControlsLanguagesForProject($id: ID!) {
    getContent(id: $id) {
      ... on Node {
        id
        __typename
      }
      ... on ContentWatchable {
        muteAllSwearsAvailability
      }
    }
  }
`

interface GetParentalControlsForContent {
  client: ApolloClient
  contentId: Maybe<string>
}

export const getParentalControlsForContent = async ({
  client,
  contentId,
}: GetParentalControlsForContent): Promise<GetParentalControlsLanguagesForContentQuery | null> => {
  try {
    if (!contentId) return null

    const result = await client.query<
      GetParentalControlsLanguagesForContentQuery,
      GetParentalControlsLanguagesForContentQueryVariables
    >({
      query: GET_PARENTAL_CONTROLS_LANGUAGES_FOR_CONTENT,
      variables: { id: contentId },
      context: { requireAuth: true },
      errorPolicy: 'all',
    })

    return {
      getContent: result?.data?.getContent ?? null,
      user: result?.data?.user ?? null,
    }
  } catch (error) {
    logger().error('getParentalControlsForContent failed', { error, contentId })
    return null
  }
}

export type ParentalControlsContent = NonNullable<GetParentalControlsLanguagesForContentQuery['getContent']>

export const getParentalControlsForProject = async ({
  client,
  contentId,
}: GetParentalControlsForContent): Promise<GetParentalControlsLanguagesForProjectQuery | null> => {
  try {
    if (!contentId) return null

    const result = await client.query<
      GetParentalControlsLanguagesForProjectQuery,
      GetParentalControlsLanguagesForProjectQueryVariables
    >({
      query: GET_PARENTAL_CONTROLS_LANGUAGES_FOR_PROJECT,
      variables: { id: contentId },
      errorPolicy: 'all',
    })

    return {
      getContent: result?.data?.getContent ?? null,
    }
  } catch (error) {
    logger().error('getParentalControlsForProject failed', { error, contentId })
    return null
  }
}

export const getSkipsEnabledFromParentalControls = (
  parentalControls: ParentalControlsContent | undefined | null,
): boolean => {
  if (!parentalControls) return false
  const availability =
    parentalControls && 'muteAllSwearsAvailability' in parentalControls
      ? parentalControls.muteAllSwearsAvailability
      : null
  if (!Array.isArray(availability)) return false
  const skipsLanguages: (string | null)[] = availability.map((availableLocale: string | null | undefined) =>
    availableLocale ? availableLocale : null,
  )
  return skipsLanguages.length > 0
}
